import {
  answerFieldErrors,
  buildAnswerSchema,
  type FormDoc,
} from "@koaform/form-schema";
import {
  fontStack,
  FormMedia,
  FormRenderer,
  resolveThemeUrl,
  themeVars,
} from "@koaform/ui";
import { useEffect, useState } from "react";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

/**
 * The draft, exactly as a respondent will see it.
 *
 * Uses the SAME FormRenderer and the SAME answer validation the public page
 * uses, so the preview cannot drift from reality: pressing Submit runs the real
 * wire-schema checks and then shows the real confirmation — it just never
 * leaves the browser.
 */
export const PreviewDialog = ({
  doc,
  onClose,
}: {
  doc: FormDoc;
  onClose: () => void;
}) => {
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = () => {
    const { __honeypot: _drop, ...rest } = answers;
    const parsed = buildAnswerSchema(doc).safeParse(rest);
    if (!parsed.success) {
      // The same fieldId-keyed mapping the API's 400 responses use.
      setErrors(answerFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setDone(true);
  };

  const change = (fieldId: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const { [fieldId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const confirmation = doc.settings.confirmation;
  const theme = doc.settings.theme;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Preview"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade absolute inset-0 z-50 overflow-auto bg-ink/45 p-6 backdrop-blur-[3px]"
      style={{ ...themeVars(theme), fontFamily: fontStack(theme.font) }}
    >
      <div className="mx-auto flex max-w-[620px] items-center justify-between pb-4">
        <span className="font-mono text-[11px] tracking-[0.1em] text-white/80">
          PREVIEW · NOTHING IS SENT
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[9px] bg-white px-3 py-1.5 text-sm font-semibold text-ink hover:bg-primary-100"
        >
          Close
        </button>
      </div>

      {/* The frame stands in for the page, so a custom page color shows. */}
      <div
        className="animate-rise mx-auto max-w-[680px] rounded-[24px] p-4 sm:p-7"
        style={{ backgroundColor: theme.background ?? "var(--color-paper)" }}
      >
        <FormMedia
          className="mb-4"
          bannerUrl={resolveThemeUrl(theme.bannerUrl, API_URL)}
          logoUrl={resolveThemeUrl(theme.logoUrl, API_URL)}
        />
        <div className="rounded-[20px] border border-line-strong bg-surface p-8 shadow-[0_18px_50px_-34px_rgba(12,26,46,0.4)] sm:p-11">
          {done ? (
            <div className="animate-rise text-center">
              <div className="mx-auto mb-5 flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-primary-100 text-2xl font-bold text-primary-600">
                ✓
              </div>
              <h2 className="m-0 mb-2.5 text-[27px] font-extrabold tracking-[-0.03em]">
                Thanks — got it.
              </h2>
              <p className="m-0 text-[16px] leading-[1.55] text-muted">
                {confirmation.type === "message"
                  ? confirmation.message
                  : `Respondents will be redirected to ${confirmation.url}`}
              </p>
              <button
                type="button"
                onClick={() => {
                  setAnswers({});
                  setDone(false);
                }}
                className="mt-6 rounded-[10px] border border-line-bold bg-surface px-4 py-2.5 text-sm font-semibold text-ink hover:border-primary-600"
              >
                Fill it in again
              </button>
            </div>
          ) : (
            <>
              <h1 className="m-0 mb-2.5 text-[31px] leading-[1.12] font-extrabold tracking-[-0.035em]">
                {doc.title}
              </h1>
              {doc.description ? (
                <p className="m-0 text-[16px] leading-[1.55] text-muted">
                  {doc.description}
                </p>
              ) : null}

              {/* Same rhythm as the public page: air before the first
                  question whether or not there is a description. */}
              <div className="mt-[30px]">
                <FormRenderer
                  doc={doc}
                  answers={answers}
                  errors={errors}
                  onChange={change}
                  onSubmit={submit}
                  submitting={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
